import React, { useState, useEffect } from "react";
import Layout from "../Layout/Layout";
import PageHeader from "../PageHeader/PageHeader";
import Pager from "./Pager";
import Portal from "../../stacks/portal/PortalAWS";
import SearchCardList from "./SearchCardList";
import SearchCriteria from "./SearchCriteria";
import searchCriteria from "../../data/searchCriteria";
import { FaSearch } from "react-icons/fa";
import { Input, Row, Col, Container, Form } from "reactstrap";
import { useErrorToast } from "../../hooks/useErrorToast";

function buildCriterion(criterion) {
  let rv = {
    [criterion.name]: {
      title: criterion.label,
      name: criterion.name,
    },
  };
  if (criterion.checkbox || criterion.radio) {
    rv[criterion.name].choices = criterion.selectValues.map((item, index) => {
      return {
        key: item,
        title: criterion.fieldLabel[index],
        value: false,
      };
    });
  }
  return rv;
}

// Note: items are not a range check, matches SOF interest
const yearIncorporatedAttributes = {
  yearIncorporated: {
    title: "Incorporated",
    choices: [
      { key: '"<1990"', value: false, title: "<1990" },
      { key: '"<2000"', value: false, title: "<2000" },
      { key: '"<2010"', value: false, title: "<2010" },
    ],
  },
};

// Note: items are not a range check, matches SOF interest
const employeesAttributes = {
  employees: {
    title: "Employees",
    choices: [
      { key: '"<10"', value: false, title: "<10" },
      { key: '"<100"', value: false, title: "<100" },
      { key: '"<1000"', value: false, title: "<1000" },
    ],
  },
};

// Note: items are not a range check, matches SOF interest
const fundingAmountCurrency = {
  fundingAmountCurrency: {
    title: "Funding Amount",
    choices: [
      { key: '"<10000"', value: false, title: "<$10,000" },
      { key: '"<100000"', value: false, title: "<$100,000" },
      { key: '"<1000000"', value: false, title: "<$1,000,000" },
      { key: '"<10000000"', value: false, title: "<$10,000,000" },
    ],
  },
};

const fullAttributes = {
  ...buildCriterion(searchCriteria.round),
  ...buildCriterion(searchCriteria.regType),
  ...buildCriterion(searchCriteria.securityTypes),
  ...buildCriterion(searchCriteria.legalForm),
  ...buildCriterion(searchCriteria.revenueProducing),
  ...buildCriterion(searchCriteria.profitable),
  ...buildCriterion(searchCriteria.industryNaics),
  ...yearIncorporatedAttributes,
  ...employeesAttributes,
  ...fundingAmountCurrency,
};

const SearchScreen = (props) => {
  const { setErrorMsg, errorToast } = useErrorToast("");
  const [searchString, setSearchString] = useState(props.searchString ? props.searchString : '');
  const [from, setFrom] = useState(0);
  const [total, setTotal] = useState();
  const [content, setContent] = useState({});
  const [attributes, setAttributes] = useState(fullAttributes);

  const [pageSize] = useState(12);
  const [pagesDisplayed] = useState(12);

  async function query(fm, attributes, search=searchString) {
    try {
      props.updateSearchString && props.updateSearchString(search)
      if (!fm) {
        setFrom(0);
        fm = 0;
      }
      if (!attributes) {
        attributes = generateAttributesSearch();
      }

      const portal = Portal.getInstance();
      setErrorMsg("");
      let attributedSearchString = search;
      if (attributes.trim()) {
        if (attributedSearchString.trim()) {
          attributedSearchString += " AND ";
        }
        attributedSearchString += attributes;
      }

      const data = await portal.searchSOF(attributedSearchString, pageSize, fm !== undefined ? fm : from)
      setTotal((data.hits && data.hits.total && data.hits.total.value) || 0)
      setContent(data)
    }
    catch (err) {
      console.error(`INFO DreamEx Connect ${err.message}`)
      setErrorMsg(err.message)
    }
  }

  useEffect(() => {
    query();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attributes]);

  function queryFirst() {
    const newFrom = 0;
    setFrom(newFrom);
    query(newFrom);
  }

  function queryPrev() {
    const newFrom = from - pageSize > 0 ? from - pageSize : 0;
    setFrom(newFrom);
    query(newFrom);
  }

  function queryPage(page) {
    const pageFrom = page * pageSize;
    if (pageFrom < total) {
      setFrom(pageFrom);
      query(pageFrom);
    }
  }

  function queryNext() {
    const newFrom = from + pageSize < total ? from + pageSize : from;
    setFrom(newFrom);
    query(newFrom);
  }

  function queryLast() {
    const newFrom = Math.trunc(total / pageSize) * pageSize;
    setFrom(newFrom);
    query(newFrom);
  }

  function morePages() {
    const totalPages = Math.trunc((total + pageSize - 1) / pageSize);
    const basePage = from / pageSize;
    if (basePage < totalPages - pagesDisplayed) {
      setFrom((basePage + pagesDisplayed) * pageSize);
    }
  }

  function generateSingleAttributeSearch(values) {
    const search = values.choices.reduce((current, { key, value }) => {
      let or = current !== "" ? " OR " : "";
      if (value) {
        return `${current}${or}${key}`;
      }
      return current;
    }, "");
    return search;
  }

  function generateAttributesSearch() {
    const search = Object.entries(attributes).reduce(
      (current, [key, attributeValue]) => {
        const criteria = generateSingleAttributeSearch(attributeValue);
        if (criteria) {
          let and = current !== "" ? " AND " : "";
          return `${current}${and}${key}: (${criteria})`;
        }
        return current;
      },
      ""
    );
    return search;
  }

  function attributeOnChange(name, value) {
    const newValue = { ...attributes[name], choices: value };
    setAttributes({ ...attributes, [name]: newValue });
  }

  function select(sofId, source) {
    props.setCurrentCompany && props.setCurrentCompany(sofId)
    props.history.push(`/portal-message/${sofId}`)
  }

  function submitHandler(e) {
    e.preventDefault()
    query()
  }
  
  return (
    <Layout product='portal'>
      <PageHeader title='DreamEx Connect Source Of Funds Search'/>
      <Container fluid={true}>
        <div style={{ height: "1em" }} />
        <Row>
          <Col md={3}>
            <SearchCriteria value={attributes} onChange={attributeOnChange} />
          </Col>
          <Col md={9}>
              {/* {attributeSearch} */}
              <Row>
                <Col md={10}>
                  <Form onSubmit={submitHandler}>
                    <Input
                      value={searchString}
                      onChange={(e) => setSearchString(e.target.value)}
                    />
                  </Form>

                </Col>
                <Col md={2} >
                  <span onClick={() => query()}>
                    <FaSearch />
                  </span>
                  <span
                    style={{fontWeight: 'bold', textAlign: 'left', cursor: 'default'}}
                    onClick={ () => {
                        setSearchString(''); query(0, generateAttributesSearch(), '')
                    }}
                  >
                    &nbsp;&nbsp;Clear
                  </span>
                </Col>
              </Row>
              <Row>
                <Col>
                  {total === 0 ? (
                    "Nothing Found"
                  ) : (
                    <Pager
                      basePage={from / pageSize}
                      pagesDisplayed={
                        (total - from) / pageSize < pagesDisplayed
                          ? (total - from) / pageSize
                          : pagesDisplayed
                      }
                      first={queryFirst}
                      prev={queryPrev}
                      page={queryPage}
                      more={morePages}
                      next={queryNext}
                      last={queryLast}
                    />
                  )}
                </Col>
              </Row>
              {/* <Row>
                <Col>Total: {total} From: {from} basePage: {from/pageSize}
                </Col>
              </Row> */}
              <Row>
                <Col>
                  {content && content.hits && content.hits.hits && (
                    <SearchCardList hits={content.hits.hits} onClick={select} />
                  )}
                </Col>
              </Row>
          </Col>
        </Row>
      </Container>
      {errorToast}
    </Layout>
  );
};

export default SearchScreen;
